import log from 'loglevel'

/**
 * Application logger utility built on loglevel.
 * Provides structured logging with configurable levels.
 *
 * Log levels (from most to least verbose):
 * - trace: Very detailed debugging
 * - debug: Debugging information
 * - info: General information
 * - warn: Warning messages
 * - error: Error messages
 *
 * In production, set level to 'warn' or 'error' to reduce noise.
 * In development, use 'debug' or 'trace' for detailed output.
 */

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent'

const DEFAULT_LEVEL: LogLevel =
  typeof window !== 'undefined' && window.location?.hostname === 'localhost' ? 'debug' : 'warn'

log.setLevel(DEFAULT_LEVEL)

/**
 * Set the logging level for the application.
 * Messages below this level will not be displayed.
 */
export const setLogLevel = (level: LogLevel): void => {
  log.setLevel(level)
}

/**
 * Get the current logging level.
 */
export const getLogLevel = (): string => {
  const levelNames = ['trace', 'debug', 'info', 'warn', 'error', 'silent']
  return levelNames[log.getLevel()] ?? 'unknown'
}

/**
 * Log a trace message (most verbose).
 */
export const trace = (...args: unknown[]): void => {
  log.trace(...args)
}

/**
 * Log a debug message.
 */
export const debug = (...args: unknown[]): void => {
  log.debug(...args)
}

/**
 * Log an info message.
 */
export const info = (...args: unknown[]): void => {
  log.info(...args)
}

/**
 * Log a warning message.
 */
export const warn = (...args: unknown[]): void => {
  log.warn(...args)
}

/**
 * Log an error message.
 */
export const error = (...args: unknown[]): void => {
  log.error(...args)
}

/**
 * Logger object with all logging methods.
 * Alternative to importing individual functions.
 */
export const logger = {
  trace,
  debug,
  info,
  warn,
  error,
  setLevel: setLogLevel,
  getLevel: getLogLevel,
}
